/*
  component: post-detail
  title: Post Detail Web Component - for viewing individual posts
  methods:
    - setCurrentUser: set the current user
  deps:
    - post-item
*/
class PostDetail extends HTMLElement {
  constructor() {
    super();
    this.postId = null;
    this.post = null;
    this.isLoading = true;
    this.error = null;
    this._currentUser = null;
  }

  connectedCallback() {
    this.render();
    this.loadPost();
    this.attachEventListeners();
  }

  setCurrentUser(data) {
    this._currentUser = data;
  }

  static get observedAttributes() {
    return ['post-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'post-id' && newValue !== oldValue) {
      this.postId = newValue;
      this.loadPost();
    }
  }

  render() {
    this.innerHTML = `
      <div class="post-detail">
        <div class="post-detail-header">
          <button class="btn btn-outline back-btn" id="backBtn">
            ← Back to Feed
          </button>
          <h2>Post Details</h2>
        </div>

        <div class="post-detail-content">
        </div>        
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading post...</p>
        </div>
      `;
    }
    return '<post-item></post-item>';
  }

  attachEventListeners() {
    // Back button
    const backBtn = this.querySelector('#backBtn');
    backBtn?.addEventListener('click', (e) => {
      e.preventDefault(); // Prevent any default behavior

      // Always go to main feed, not browser back
      if (window.router) {
        window.router.navigate('/');
      } else {
        // Fallback to direct navigation
        window.location.href = '/';
      }
    });
  }

  async loadPost() {
    if (!this.postId) return;

    this.isLoading = true;
    this.error = null;
    await this.updateContent();

    try {
      const response = await window.API.request(`/posts/${this.postId}`);
      this.post = response;
      this.error = null;
    } catch (error) {
      window.logger.error('Failed to load post:', error);
      this.post = null;

      if (error.status === 404) {
        this.error = 'This post doesn\'t exist or you don\'t have permission to view it.';
      } else {
        this.error = 'Failed to load the post. Please try again.';
      }
    } finally {
      this.isLoading = false;
      await this.updateContent();
    }
  }

  async updateContent() {
    const contentContainer = this.querySelector('.post-detail-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderContent();
      const postItem = contentContainer.querySelector('post-item');
      if (!postItem) {
        return;
      }
      if (typeof postItem.setCurrentUser === 'function') {
        window.logger.debug('Setting currentUser on post-item');
        postItem.setCurrentUser(this._currentUser);
      } else {
        window.logger.warn('post-item does not have setCurrentUser method');
      }

      postItem.postData = this.post;
      postItem.expandComments = true;
    }
  }

  copyPermalink() {
    const permalinkUrl = `${window.location.origin}/post/${this.postId}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(permalinkUrl).then(() => {
        this.showSuccess('Permalink copied to clipboard!');
      }).catch(() => {
        this.fallbackCopyToClipboard(permalinkUrl);
      });
    } else {
      this.fallbackCopyToClipboard(permalinkUrl);
    }
  }

  fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showSuccess('Permalink copied to clipboard!');
    } catch (err) {
      window.logger.error('Failed to copy permalink.', err);
      this.showError('Failed to copy permalink. Please copy manually: ' + text);
    }

    document.body.removeChild(textArea);
  }

  editPost() {
    this.handleEdit();
  }

  async reportPost() {
    const postId = this.postId || this.post?.id;
    if (!postId) {
      window.logger.error('Cannot report post: no post ID');
      return;
    }

    // Show report modal
    const reason = prompt('Why are you reporting this post?\n\nPlease provide a brief reason (e.g., "Inappropriate content", "Spam", "Harassment", etc.):');

    if (!reason || reason.trim().length === 0) {
      return; // User cancelled or didn't provide reason
    }

    const description = prompt('Additional details (optional):\n\nProvide any additional context that might help us understand the issue:') || '';

    try {
      const response = await window.API.request('/support/flag-post', {
        method: 'POST',
        body: JSON.stringify({
          post_id: postId,
          reason: reason.trim(),
          description: description.trim()
        })
      });

      this.showSuccess('Thank you for reporting this post. Our team will review it shortly.');

      // Optionally open the safety armadillo to show the created ticket
      const safetyArmadillo = document.querySelector('safety-armadillo');
      if (safetyArmadillo && response.ticket) {
        safetyArmadillo.open();
        setTimeout(() => {
          safetyArmadillo.viewTicket(response.ticket.id || response.ticket.conversation_id);
        }, 500);
      }
    } catch (error) {
      window.logger.error('Failed to report post:', error);
      this.showError(`Failed to report post: ${error.message || 'Please try again later.'}`);
    }
  }

  formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  showSuccess(message) {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.textContent = message;
    successDiv.style.position = 'fixed';
    successDiv.style.top = '20px';
    successDiv.style.right = '20px';
    successDiv.style.zIndex = '10000';
    successDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(successDiv);

    setTimeout(() => successDiv.remove(), 4000);
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 4000);
  }
}

// Register the custom element
customElements.define('post-detail', PostDetail);
